import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { ABOUT_IMAGES, HERO_IMAGES } from "@/lib/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export const DEFAULT_TEXTS = {
    hero_overline: "Dal 1996 · Impresa Edile di Pregio",
    hero_title_1: "Architettura,",
    hero_title_2: "restauro e",
    hero_title_3: "ville d'eccellenza.",
    hero_paragraph:
        "Da trent'anni costruiamo e ristrutturiamo ville, appartamenti, piscine e dimore di pregio — dal recupero dei rustici in pietra alle strutture alberghiere — con professionisti qualificati, mezzi innovativi e attrezzature all'avanguardia.",
    hero_cta_portfolio: "Scopri i nostri progetti",
    hero_cta_contact: "Prenota un Sopralluogo",
    marquee_items:
        "Ville di Pregio\nRestauro Rustici e Dimore Storiche\nPiscine Coreografiche\nBoutique Hotel & Resort\nFiniture d'Arte\nCantiere Chiavi in Mano",
    about_eyebrow: "Chi Siamo",
    about_heading: "Trent'anni sul territorio, una sola parola d'ordine: la qualità.",
    about_p1:
        "Santa Lucia Quattro nasce nel 1996 come impresa a conduzione familiare e cresce cantiere dopo cantiere nell'ambito privato: ville, appartamenti, piscine e immobili di pregio, fino alle strutture alberghiere.",
    about_p2:
        "La nostra specialità è il recupero e il restauro dei rustici: pietra a vista, travi secolari e volte antiche tornano a vivere grazie a professionisti qualificati e a un parco mezzi moderno e performante.",
    about_caption: "Bottega di cantiere",
    about_quote: "La mano dell'artigiano, il rigore del cantiere.",
    about_badge: "Est.\n1996",
    services_heading: "Ogni cantiere, un'opera su misura.",
    services_subtitle:
        "Cinque ambiti di intervento, un unico metodo: sopralluogo accurato, progettazione condivisa, esecuzione impeccabile.",
    portfolio_heading: "Opere che parlano da sole.",
    testimonials_heading: "La fiducia di chi ha già costruito con noi.",
    whyus_heading: "L'affidabilità si misura in decenni.",
    whyus_paragraph:
        "Trent'anni di cantieri portati a termine, clienti che tornano e ci consigliano. È questa la garanzia più concreta che possiamo offrirti.",
    contact_heading: "Parliamo del tuo progetto.",
    contact_paragraph:
        "Raccontaci l'immobile e le tue idee: organizziamo un sopralluogo gratuito e ti consegniamo un preventivo analitico senza impegno.",
    servizi_page_title: "I nostri servizi, nel dettaglio.",
    servizi_page_intro:
        "Ogni intervento nasce da un sopralluogo accurato e da un preventivo analitico. Ecco come lavoriamo, ambito per ambito.",
};

export const DEFAULT_PILLARS = [
    { title: "Tradizione Artigianale", desc: "Scarpellini e mastro muratori formati nelle botteghe storiche del territorio." },
    { title: "Attrezzature all'Avanguardia", desc: "Mezzi innovativi, rilievi laser e tecnologie di cantiere di ultima generazione." },
    { title: "Precisione nei Tempi", desc: "Cronoprogrammi rispettati al giorno, con un unico referente per tutto il cantiere." },
];

export const DEFAULT_WHYUS = {
    stats: [
        { value: 30, suffix: "+", label: "Anni di Esperienza" },
        { value: 180, suffix: "+", label: "Progetti Completati" },
        { value: 100, suffix: "%", label: "Consegne Puntuali" },
        { value: 45, suffix: "+", label: "Maestranze Qualificate" },
    ],
    credentials: [
        { title: "Certificazione SOA & ISO 9001", desc: "Processi di cantiere certificati e tracciati, dalla fondazione al collaudo." },
        { title: "Rilievo e Computo Metrico", desc: "Preventivi analitici voce per voce, senza sorprese in corso d'opera." },
        { title: "Referente Unico di Cantiere", desc: "Un direttore tecnico segue il tuo progetto dal sopralluogo alla consegna." },
    ],
};

export const DEFAULT_SERVICE_CARDS = [
    {
        title: "Ville & Residenze d'Epoca",
        desc: "Costruzione e ristrutturazione di ville singole, appartamenti di pregio e dimore padronali con finiture di alto livello.",
        photo: "https://images.pexels.com/photos/17010997/pexels-photo-17010997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        title: "Restauro & Recupero Rustici",
        desc: "Conservazione di casali storici, rustici in pietra e volte antiche con metodologie conservative e materiali d'epoca.",
        photo: "https://images.unsplash.com/photo-1704750843798-e7655f99a643?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxoaXN0b3JpYyUyMGl0YWxpYW4lMjBzdG9uZSUyMGhvdXNlJTIwcmVzdG9yYXRpb24lMjBydXN0aWNvfGVufDB8fHx8MTc5MDMyNzM4OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Piscine Architettoniche & SPA",
        desc: "Piscine a sfioro, vasche scenografiche e spazi benessere esterni integrati al paesaggio.",
        photo: "",
    },
    {
        title: "Hotellerie & Strutture Ricettive",
        desc: "Interventi per boutique hotel e dimore storiche con minimi tempi di fermo operativo.",
        photo: "",
    },
    {
        title: "Cantiere Chiavi in Mano",
        desc: "Coordinamento completo con architetti, ingegneri e maestranze, dalla burocrazia alle finiture d'interni.",
        photo: "",
    },
];

export const DEFAULT_INSTAGRAM = "https://instagram.com/santaluciaquattro";

const DEFAULT_SITE = {
    texts: DEFAULT_TEXTS,
    pillars: DEFAULT_PILLARS,
    whyus: DEFAULT_WHYUS,
    serviceCards: DEFAULT_SERVICE_CARDS,
    instagram: DEFAULT_INSTAGRAM,
    heroImage: HERO_IMAGES.frame,
    ready: false,
    images: { about_main: ABOUT_IMAGES.artisan, about_secondary: ABOUT_IMAGES.blueprint },
};

const SiteContext = createContext(DEFAULT_SITE);

export const TextsProvider = ({ children }) => {
    const [site, setSite] = useState(DEFAULT_SITE);

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
            .then((res) => {
                const d = res.data || {};
                setSite({
                    texts: { ...DEFAULT_TEXTS, ...(d.texts || {}) },
                    pillars:
                        Array.isArray(d.about_pillars) && d.about_pillars.length
                            ? d.about_pillars
                            : DEFAULT_PILLARS,
                    whyus:
                        d.whyus && Array.isArray(d.whyus.stats) && d.whyus.stats.length
                            ? {
                                  stats: d.whyus.stats,
                                  credentials:
                                      Array.isArray(d.whyus.credentials) && d.whyus.credentials.length
                                          ? d.whyus.credentials
                                          : DEFAULT_WHYUS.credentials,
                              }
                            : DEFAULT_WHYUS,
                    serviceCards:
                        Array.isArray(d.services_cards) && d.services_cards.length
                            ? d.services_cards
                            : DEFAULT_SERVICE_CARDS,
                    instagram: d.instagram_url || DEFAULT_INSTAGRAM,
                    heroImage: resolveImg(d.hero_image) || HERO_IMAGES.frame,
                    ready: true,
                    images: {
                        about_main: resolveImg(d.about_image_main) || ABOUT_IMAGES.artisan,
                        about_secondary: resolveImg(d.about_image_secondary) || ABOUT_IMAGES.blueprint,
                    },
                });
            })
            .catch(() => setSite((s) => ({ ...s, ready: true })));
    }, []);

    return <SiteContext.Provider value={site}>{children}</SiteContext.Provider>;
};

export const useTexts = () => useContext(SiteContext).texts;

export const useExtras = () => {
    const v = useContext(SiteContext);
    return {
        pillars: v.pillars,
        whyus: v.whyus,
        images: v.images,
        serviceCards: v.serviceCards,
        instagram: v.instagram,
        heroImage: v.heroImage,
        ready: v.ready,
    };
};
